import React, { useState, useEffect, useRef } from 'react';
import { ImCancelCircle } from 'react-icons/im';
import { FaEdit } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import AccountApi from '../api/AccountApi';
import AccountForm from './AccountForm';
import Pagination from '../component/paginations/pagination';

const AccountList = ({ refresh }) => {
    const [state, setState] = useState([]);
    const [showDetails, setShowDetails] = useState(false);
    const taskDetailsRef = useRef(null);
    const [editAccountId, setEditAccountId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [postsPerPage, setPostsPerPage] = useState(10);

    useEffect(() => {
        getAccount();
    }, [refresh]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (taskDetailsRef.current && !taskDetailsRef.current.contains(event.target)) {
                setShowDetails(false);
            }
        };

        if (showDetails) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDetails]);

    const getAccount = async () => {
        const res = await AccountApi.index();
        if (Array.isArray(res)) {
            setState(res);
        } else {
            console.error("API response is not an array:", res);
        }
    };

    const deleteAccount = async (id) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this account?');
        if (confirmDelete) {
            const res = await AccountApi.delete(id);
            if (res) {
                getAccount();
            }
        }
    };

    const toggleDetails = (accountId = null) => {
        setShowDetails(!showDetails);
        setEditAccountId(accountId);
    };

    const lastPostIndex = currentPage * postsPerPage;
    const firstPostIndex = lastPostIndex - postsPerPage;
    const currentPosts = state.slice(firstPostIndex, lastPostIndex);

    const handlePostsPerPageChange = (e) => {
        const newPostsPerPage = parseInt(e.target.value);
        setPostsPerPage(newPostsPerPage);
        setCurrentPage(1); // Reset to the first page when changing posts per page
    };

    return (
        <div className="mx-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b bg-white">
                <div className="text-lg text-gray-500">
                    <h1>All Accounts</h1>
                </div>
                <div>
                    <button
                        onClick={() => toggleDetails()}
                        ref={taskDetailsRef}
                        className="bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-400 rounded shadow "
                    >
                        Add
                    </button>
                </div>
            </div>

            <div className="flex flex-col">
                <div className="overflow-x-auto shadow-md sm:rounded-lg">
                    <div className="inline-block min-w-full align-middle">
                        <div className="overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200 table-fixed dark:divide-gray-700">
                                <thead className="bg-gray-100 dark:bg-gray-700">
                                    <tr>
                                        <th scope="col" className="p-4">
                                            <div className="flex items-center">
                                                <input
                                                    id="checkbox-all"
                                                    type="checkbox"
                                                    className="w-4 h-4 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                                />
                                                <label htmlFor="checkbox-all" className="sr-only">
                                                    checkbox
                                                </label>
                                            </div>
                                        </th>
                                        <th scope="col" className="py-3 px-6 text-xs font-medium tracking-wider text-left text-gray-700 uppercase dark:text-gray-400">
                                            Account Name
                                        </th>
                                        <th scope="col" className="py-3 px-6 text-xs font-medium tracking-wider text-left text-gray-700 uppercase dark:text-gray-400">
                                            Bank
                                        </th>
                                        <th scope="col" className="py-3 px-6 text-xs font-medium tracking-wider text-left text-gray-700 uppercase dark:text-gray-400">
                                            Opening Balance
                                        </th>
                                        <th scope="col" className="py-3 px-6 text-xs font-medium tracking-wider text-left text-gray-700 uppercase dark:text-gray-400">
                                            Current Balance
                                        </th>
                                        <th scope="col" className="p-4">
                                            <span className="sr-only">Edit</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                                    {currentPosts.map((account, index) => (
                                        <tr
                                            key={index}
                                            className="hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                            <td className="p-4 w-4">
                                                <div className="flex items-center">
                                                    <input
                                                        id="checkbox-table-1"
                                                        type="checkbox"
                                                        className="w-4 h-4 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                                    />
                                                    <label htmlFor="checkbox-table-1" className="sr-only">
                                                        checkbox
                                                    </label>
                                                </div>
                                            </td>
                                            <td className=" px-6 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                                {account.name}
                                            </td>
                                            <td className=" px-6 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                                {account.bank_name}
                                            </td>
                                            <td className=" px-6 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                                ${account.opening_balance ?? 0}
                                            </td>
                                            <td className=" px-6 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                                ${Number(account.current_balance ?? 0).toFixed(2)}
                                            </td>
                                            <td className=" p-3 text-sm font-medium float-right whitespace-nowrap flex my-auto">
                                                <a
                                                    onClick={() => toggleDetails(account.id)}
                                                    ref={taskDetailsRef}
                                                    href="#"
                                                    title='Edit'
                                                    className="text-blue-600 dark:text-blue-500 hover:underline text-2xl"
                                                >
                                                    <FaEdit />
                                                </a>
                                                <button
                                                    title='Delete'
                                                    onClick={() => deleteAccount(account.id)}
                                                    className="ml-2 text-blue-600 dark:text-blue-500 hover:underline text-2xl"
                                                >
                                                    <MdDelete />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                {state.length > 0 &&
                <div className="flex justify-end items-center mt-4 gap-4">
                    <div className="flex items-center border border-gray-300 rounded-lg px-4 py-1 bg-white shadow-sm">
                        <p className="mr-2 text-gray-700">Per Page</p>
                        <select
                            className="appearance-none pl-2 pr-5 py-1 text-gray-700 focus:outline-none focus:border-indigo-500"
                            value={postsPerPage}
                            onChange={handlePostsPerPageChange}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                            <option value={50}>50</option>
                        </select>
                    </div>

                    <Pagination
                        totalPosts={state.length}
                        postsPerPage={postsPerPage}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                    />
                </div>
                }
                {showDetails && (
                    <div
                        className={`fixed z-10 top-8 md:top-8 right-0 w-full h-full shadow-2xl max-w-[50rem] bg-white transition-transform duration-300 overflow-y-auto  ${
                            showDetails ? 'transform translate-x-0' : 'transform translate-x-full'
                        }`}
                        ref={taskDetailsRef}
                    >
                        <div className="flex justify-between p-3">
                            <a
                                className="mt-2 !ml-2 text-2xl text-gray-400 cursor-pointer"
                                onClick={toggleDetails}
                            >
                                <ImCancelCircle />
                            </a>
                        </div>
                        <div>
                            <AccountForm
                                accountId={editAccountId}
                                onSubmits={getAccount}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountList;
